// Shared create/edit page for outreachEmailTemplate. Mounted at both
// /admin/email-templates/new (no :id, create on save) and
// /admin/email-templates/:id (edit existing, patch on save).

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import client from '../../../lib/feathers';
import EmailEditor from '../../../components/EmailEditor';

export default function EmailTemplateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [variables, setVariables] = useState([]);

  useEffect(() => {
    let cancelled = false;
    client.service('admin/email-variable-catalog').find({})
      .then((r) => { if (!cancelled) setVariables(r.data || []); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;
    setLoading(true);
    client.service('admin/outreach-email-template').get(id)
      .then((row) => {
        if (cancelled) return;
        setName(row.name || '');
        setDescription(row.description || '');
        setSubject(row.subject || '');
        setBodyHtml(row.bodyHtml || '');
        setIsActive(!!row.isActive);
      })
      .catch((err) => { toast.error(err.message || 'Failed to load template'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const save = async () => {
    if (!name.trim() || !subject.trim() || !bodyHtml.trim()) {
      toast.error('Name, subject, and body are all required.');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await client.service('admin/outreach-email-template').patch(id, {
          name: name.trim(),
          description: description.trim() || null,
          subject,
          bodyHtml,
          isActive,
        });
        toast.success('Template saved.');
      } else {
        const created = await client.service('admin/outreach-email-template').create({
          name: name.trim(),
          description: description.trim() || null,
          subject,
          bodyHtml,
        });
        toast.success('Template created.');
        navigate(`/admin/email-templates/${created.id}`, { replace: true });
        return;
      }
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const retire = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Retire this template? It will hide from compose pages but stay in the list.')) return;
    setSaving(true);
    try {
      await client.service('admin/outreach-email-template').remove(id);
      toast.success('Template retired.');
      navigate('/admin/email-templates');
    } catch (err) {
      toast.error(err.message || 'Retire failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6"><p className="text-gray-500">Loading…</p></div>;

  return (
    <div className="space-y-4">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Edit template' : 'New template'}
            </h2>
            <Link to="/admin/email-templates" className="text-sm text-blue-700 hover:underline">
              ← Back to templates
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {isEdit && (
              <button
                type="button"
                onClick={retire}
                disabled={saving}
                className="px-4 py-2 text-sm text-red-700 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50"
              >
                Retire
              </button>
            )}
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
            </button>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
              placeholder="Welcome email"
              maxLength={120}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
              placeholder="Short note about when to use this template"
              maxLength={240}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
              placeholder="Welcome to VinoSeeker, {{firstName}}"
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">
              {'{{firstName}} and other variables work in the subject too.'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
            <EmailEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              variables={variables}
              placeholder="Write the body…"
              minHeight="360px"
            />
          </div>
          {isEdit && (
            <div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="rounded"
                />
                Active (show in compose pickers)
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
